/**
 * The physical pages one OcrResult actually covers.
 *
 * `OcrResultResource` serializes the CONTAINER's whole page list onto every
 * result — the frontend needs it to render page images — so `pages` cannot be
 * read as "this result's pages". A pool run or a per-page template assignment
 * splits a container across several results, and asking for a per-page
 * rejection reason off `pages` produced a note box for every page in the
 * container on every result. Notes filed against a page the result owns
 * nothing on are worse than useless: they key into a slot nothing reads.
 *
 * Ownership follows the same signals the results panel groups by, in the same
 * page space (`document_chunks.page_number`, globally remapped by
 * `ProcessOcrPageBatchJob`, so page N is `pages[N - 1]`).
 *
 * Mirrors `ResultPageAttribution::pagesOwnedBy()` — keep the two in step.
 */
export interface OwnedPage {
  /** Global 1-based page number — the key space `page_notes` uses. */
  pageNumber: number;
  /** Display label from the container's page list ("PG-002"), when known. */
  rawLabel: string | null;
}

export function resolveOwnedPages(result: {
  page_number?: string | null;
  pages?: { page_number: string }[] | null;
  chunks?: { page_number: number }[] | null;
  extracted_data?:
    { page_number?: number | null; source_page?: number | null }[] | null;
  /** Global 1-based pages the run was DISPATCHED to read. */
  assigned_page_numbers?: number[] | null;
}): OwnedPage[] {
  const pages = result.pages ?? [];
  const label = (pageNumber: number): string | null =>
    pages[pageNumber - 1]?.page_number ?? null;

  const owned = new Set<number>();

  for (const chunk of result.chunks ?? []) {
    if (chunk.page_number != null) owned.add(chunk.page_number);
  }

  // A field the model returned without geometry resolves no chunk, so its page
  // is known only from its own signals — and that is exactly the page a
  // reviewer is most likely to have something to say about.
  for (const field of result.extracted_data ?? []) {
    if (field.page_number != null) owned.add(field.page_number);
    if (field.source_page != null) owned.add(field.source_page);
  }

  // What the run was ASKED to read outranks the container guess below, and
  // unions with what it produced rather than replacing it. Both directions are
  // real: a page can be assigned and yield nothing (an empty extraction, a
  // refusal, a crash), and a chunk can land outside the assignment (a repeating
  // template stamping a neighbour).
  const assigned = result.assigned_page_numbers;

  if (Array.isArray(assigned)) {
    for (const pageNumber of assigned) owned.add(pageNumber);

    // An empty assignment is an ANSWER, not a missing one — a pool sibling
    // whose template won no page covers nothing, and falling through to the
    // container list would hand it every page.
    return [...owned]
      .sort((a, b) => a - b)
      .map((pageNumber) => ({ pageNumber, rawLabel: label(pageNumber) }));
  }

  if (owned.size > 0) {
    return [...owned]
      .sort((a, b) => a - b)
      .map((pageNumber) => ({ pageNumber, rawLabel: label(pageNumber) }));
  }

  // Nothing to narrow with. Offering every page beats offering none — a
  // reviewer with no slot has nowhere to record why they are rejecting.
  if (pages.length > 0) {
    return pages.map((page, index) => ({
      pageNumber: index + 1,
      rawLabel: page.page_number || null
    }));
  }

  return [{ pageNumber: 1, rawLabel: result.page_number ?? null }];
}
